import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Calendar, Clock, MapPin, CreditCard, Loader, Repeat, AlertCircle, Star, Info } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase, checkSupabaseConnection } from '../../lib/supabase';
import { useNotification } from '../../context/NotificationContext';
import { bookingSchema, type BookingFormData } from '../../lib/validation';
import FormError from '../../components/FormError';
import RecurringBookingForm from '../../components/RecurringBookingForm';
import AddressBook from '../../components/AddressBook';
import BottomSheet from '../../components/BottomSheet';
import { useAuth } from '../../context/AuthContext';

interface Service {
  id: string;
  name: string;
  description: string;
  price: number;
  duration: number;
}

interface BookingSlot {
  date: string;
  time_slot: string;
  is_available: boolean;
}

export default function BookService() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [availableSlots, setAvailableSlots] = useState<BookingSlot[]>([]);
  const [isAddressBookOpen, setIsAddressBookOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<BookingFormData>({
    service_id: '',
    booking_date: '',
    booking_time: '',
    address: '',
    notes: '',
    is_recurring: false,
    recurring_frequency: 'weekly',
    recurring_end_date: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof BookingFormData, string>>>({});
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Check connection first
        const isConnected = await checkSupabaseConnection();
        if (!isConnected) {
          throw new Error('Unable to connect to the database');
        }

        setLoading(true);
        const { data, error } = await supabase
          .from('services')
          .select('*')
          .order('price');

        if (error) throw error;
        setServices(data || []);
      } catch (error) {
        console.error('Error fetching services:', error);
        showNotification('error', 'Failed to load services. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [showNotification]);

  useEffect(() => {
    // Check for edit mode
    const editBookingId = searchParams.get('edit');
    const bookingData = location.state?.booking;

    if (editBookingId && bookingData) {
      // Pre-fill form with existing booking data
      setFormData({
        service_id: bookingData.service_id,
        booking_date: bookingData.booking_date,
        booking_time: bookingData.booking_time,
        address: bookingData.address,
        notes: bookingData.notes || '',
        is_recurring: false // Editing doesn't support recurring bookings
      });
    }
  }, [searchParams, location.state]);

  useEffect(() => {
    if (formData.booking_date) {
      fetchAvailableSlots(formData.booking_date);
    }
  }, [formData.booking_date]);

  const fetchAvailableSlots = async (date: string) => {
    try {
      setLoadingSlots(true);
      
      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No session found');
      }
      
      // Call the get-available-slots edge function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-available-slots?date=${date}`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const slots = await response.json();
      setAvailableSlots(slots || []);
    } catch (error) {
      console.error('Error fetching available slots:', error);
      showNotification('error', 'Failed to load available time slots');
    } finally {
      setLoadingSlots(false);
    }
  };

  const validateForm = () => {
    try {
      bookingSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof Error) {
        const zodError = JSON.parse(error.message);
        const newErrors: typeof errors = {};
        zodError.forEach((err: any) => {
          const path = err.path[0] as keyof BookingFormData;
          newErrors[path] = err.message;
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleServiceSelect = (service: Service) => {
    setFormData(prev => ({ ...prev, service_id: service.id }));
    setSelectedService(service);
    setCurrentStep(2);
  };

  const handleAddressSelect = (address: string) => {
    setFormData(prev => ({ ...prev, address }));
    setIsAddressBookOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showNotification('error', 'Please fix the form errors before submitting');
      return;
    }

    if (!user) {
      navigate('/login', { state: { from: location } });
      return;
    }

    try {
      const bookingDateTime = new Date(`${formData.booking_date}T${formData.booking_time}`);
      const editBookingId = searchParams.get('edit');
      
      if (editBookingId) {
        // Update existing booking
        const { error: updateError } = await supabase
          .from('bookings')
          .update({
            service_id: formData.service_id,
            booking_date: bookingDateTime.toISOString(),
            address: formData.address,
            notes: formData.notes
          })
          .eq('id', editBookingId);

        if (updateError) throw updateError;

        showNotification('success', 'Booking updated successfully');
        navigate('/dashboard');
        return;
      }
      
      // Check for scheduling conflicts
      const { data: conflict } = await supabase.rpc('check_booking_conflict', {
        p_date: formData.booking_date,
        p_time: formData.booking_time,
        p_duration: selectedService?.duration || 0
      });

      if (conflict) {
        throw new Error('This time slot is no longer available');
      }

      let recurringBookingId = null;
      
      // Create recurring booking if selected
      if (formData.is_recurring && formData.recurring_frequency && formData.recurring_end_date) {
        const { data: recurringBooking, error: recurringError } = await supabase
          .from('recurring_bookings')
          .insert({
            user_id: user.id,
            service_id: formData.service_id,
            frequency: formData.recurring_frequency,
            day_of_week: new Date(formData.booking_date).getDay(),
            time_of_day: formData.booking_time,
            address: formData.address,
            notes: formData.notes,
            start_date: formData.booking_date,
            end_date: formData.recurring_end_date
          })
          .select()
          .single();

        if (recurringError) throw recurringError;
        recurringBookingId = recurringBooking.id;
      }
      
      // Create booking
      const { data: booking, error: bookingError } = await supabase
        .from('bookings')
        .insert({
          service_id: formData.service_id,
          booking_date: bookingDateTime.toISOString(),
          address: formData.address,
          notes: formData.notes,
          status: 'pending',
          user_id: user.id,
          recurring_booking_id: recurringBookingId
        })
        .select()
        .single();

      if (bookingError) throw bookingError;

      showNotification('success', 'Booking created successfully!');
      navigate('/booking-confirmation');
    } catch (error) {
      console.error('Error creating booking:', error);
      showNotification('error', error instanceof Error ? error.message : 'Failed to create booking');
    }
  };

  const goToNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-gray-800" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f0efe9] dark:bg-gray-900 pb-20">
      <div className="py-12 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-light text-gray-800 dark:text-white mb-6 text-center">Book a Service</h1>
          <div className="w-24 h-0.5 bg-gray-400 dark:bg-gray-600 mx-auto mb-8"></div>
          <div className="flex items-center justify-center max-w-md mx-auto">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 1 ? 'bg-gray-800 text-white dark:bg-gray-700' : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'}`}>
              1
            </div>
            <div className={`flex-1 h-0.5 mx-2 ${currentStep >= 2 ? 'bg-gray-800 dark:bg-gray-700' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 2 ? 'bg-gray-800 text-white dark:bg-gray-700' : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'}`}>
              2
            </div>
            <div className={`flex-1 h-0.5 mx-2 ${currentStep >= 3 ? 'bg-gray-800 dark:bg-gray-700' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep >= 3 ? 'bg-gray-800 text-white dark:bg-gray-700' : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'}`}>
              3
            </div>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
          {/* Step 1: Service Selection */}
          {currentStep === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8"
            >
              <h2 className="text-xl font-medium text-gray-800 dark:text-white mb-6">Select Service</h2>
              <div className="space-y-6">
                <div className="grid gap-6">
                  {services.map(service => (
                    <div key={service.id} className="border border-gray-200 dark:border-gray-700 p-6">
                      <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-2">{service.name}</h3>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">{service.description}</p>
                      <div className="flex justify-between items-center mb-4">
                        <div className="text-gray-600 dark:text-gray-300 flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{service.duration} minutes</span>
                        </div>
                        <div className="text-xl font-light text-gray-800 dark:text-white">
                          ${(service.price / 100).toFixed(2)}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleServiceSelect(service)}
                        className="w-full py-2 bg-gray-800 text-white dark:bg-gray-700 hover:bg-black dark:hover:bg-gray-600 transition-colors"
                      >
                        Select
                      </button>
                    </div>
                  ))}
                </div>
                {errors.service_id && <FormError message={errors.service_id} />}
              </div>
            </motion.div>
          )}

          {/* Step 2: Date and Time Selection */}
          {currentStep === 2 && selectedService && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <button 
                  type="button" 
                  onClick={goToPreviousStep}
                  className="text-gray-800 dark:text-gray-300 hover:text-black dark:hover:text-white"
                >
                  Back
                </button>
                <h2 className="text-xl font-medium text-gray-800 dark:text-white">Select Date & Time</h2>
                <div className="w-16"></div> {/* Spacer for alignment */}
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Date
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      value={formData.booking_date}
                      onChange={(e) => setFormData({ ...formData, booking_date: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-1 focus:ring-gray-800 dark:focus:ring-gray-300"
                    />
                  </div>
                  {errors.booking_date && <FormError message={errors.booking_date} />}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Time
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    {loadingSlots ? (
                      <div className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-400 dark:text-gray-500 flex items-center">
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Loading available times...
                      </div>
                    ) : (
                      <select
                        value={formData.booking_time}
                        onChange={(e) => setFormData({ ...formData, booking_time: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-1 focus:ring-gray-800 dark:focus:ring-gray-300 appearance-none"
                      >
                        <option value="">Select a time</option>
                        {availableSlots.filter(slot => slot.is_available).map((slot) => (
                          <option key={slot.time_slot} value={slot.time_slot}>
                            {new Date(`2000-01-01T${slot.time_slot}`).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: 'numeric',
                              hour12: true
                            })}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  {errors.booking_time && <FormError message={errors.booking_time} />}
                  {availableSlots.length === 0 && formData.booking_date && !loadingSlots && (
                    <div className="mt-2 text-yellow-600 flex items-center gap-2 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      No available slots for this date. Please try another date.
                    </div>
                  )}
                </div>

                {/* Recurring Booking Option */}
                <RecurringBookingForm
                  isRecurring={formData.is_recurring || false}
                  frequency={formData.recurring_frequency || 'weekly'}
                  startDate={formData.booking_date}
                  endDate={formData.recurring_end_date || ''}
                  onIsRecurringChange={(value) => setFormData({ ...formData, is_recurring: value })}
                  onFrequencyChange={(value) => setFormData({ ...formData, recurring_frequency: value })}
                  onEndDateChange={(value) => setFormData({ ...formData, recurring_end_date: value })}
                />

                <div className="pt-4">
                  <button
                    type="button"
                    onClick={goToNextStep}
                    disabled={!formData.booking_date || !formData.booking_time}
                    className="w-full py-3 bg-gray-800 text-white dark:bg-gray-700 hover:bg-black dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:bg-gray-400"
                  >
                    Continue
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Address and Notes */}
          {currentStep === 3 && selectedService && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <button 
                  type="button" 
                  onClick={goToPreviousStep}
                  className="text-gray-800 dark:text-gray-300 hover:text-black dark:hover:text-white"
                >
                  Back
                </button>
                <h2 className="text-xl font-medium text-gray-800 dark:text-white">Service Details</h2>
                <div className="w-16"></div> {/* Spacer for alignment */}
              </div>

              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Service Address
                    </label>
                    {user && (
                      <button
                        type="button"
                        onClick={() => setIsAddressBookOpen(true)}
                        className="text-gray-800 dark:text-gray-300 hover:text-black dark:hover:text-white text-sm font-medium"
                      >
                        Saved addresses
                      </button>
                    )}
                  </div>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-1 focus:ring-gray-800 dark:focus:ring-gray-300"
                    placeholder="Enter your full address including apartment/unit number"
                  />
                  {errors.address && <FormError message={errors.address} />}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-1 focus:ring-gray-800 dark:focus:ring-gray-300"
                    placeholder="Any special instructions or requirements?"
                  />
                  {errors.notes && <FormError message={errors.notes} />}
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full py-3 bg-gray-800 text-white dark:bg-gray-700 hover:bg-black dark:hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <CreditCard className="w-5 h-5" />
                    Complete Booking
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </form>
      </div>

      {/* Address Book Bottom Sheet */}
      <BottomSheet
        isOpen={isAddressBookOpen}
        onClose={() => setIsAddressBookOpen(false)}
        title="Select Address"
      >
        {user && (
          <AddressBook
            userId={user.id}
            onSelect={handleAddressSelect}
            selectable
          />
        )}
      </BottomSheet>
    </div>
  );
}